import {convertToURLDatetime} from '@domain/shared'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {
  PlanningScopeSelect as SharedPlanningScopeSelect,
  PlanningScope
} from '@widgets/planning-time-controls'
import type {Moment} from 'moment-timezone'
import {useLocation, useNavigate} from 'react-router'

import {RouteSegment, getRouteSegmentForScope} from '../../consts'
import {useSelectedTimeframe} from '../../hooks'
import type {MomentRange} from '../../types'

const mapDailyTimeframeToMonthly = (start: Moment, timezoneId: string): MomentRange => {
  return [
    start.clone().tz(timezoneId).startOf('month'),
    start.clone().tz(timezoneId).startOf('month').add(1, 'month')
  ]
}

const getStartEndParamsByScopeType = (
  scope: PlanningScope,
  timeframe: MomentRange,
  timezoneId: string
) => {
  if (scope === PlanningScope.Daily) {
    return {
      start: convertToURLDatetime(timeframe[0], timezoneId),
      end: convertToURLDatetime(timeframe[1], timezoneId)
    }
  }
  const monthlyTimeframe = mapDailyTimeframeToMonthly(timeframe[0], timezoneId)
  return {
    start: convertToURLDatetime(monthlyTimeframe[0], timezoneId),
    end: convertToURLDatetime(monthlyTimeframe[1], timezoneId)
  }
}

export const PlanningScopeSelect = () => {
  const navigate = useNavigate()
  const timezoneId = useUpmPlantTimezone()
  const {pathname} = useLocation()
  // Derive the active scope from the current route's leaf segment so the toggle
  // reflects the correct selection regardless of where the feature is mounted
  // (legacy hcem or Suite), where the parent splat/base path differs.
  const planningScope = pathname.replace(/\/+$/, '').endsWith(`/${RouteSegment.MONTHLY}`)
    ? PlanningScope.Monthly
    : PlanningScope.Daily
  const {selectedTimeframe} = useSelectedTimeframe()
  if (!selectedTimeframe) {
    throw new Error('BUG: selectedTimeframe is not defined')
  }

  return (
    <SharedPlanningScopeSelect
      data-test-id="daily_monthly_toggle_button"
      scope={planningScope}
      onChange={(type: PlanningScope) => {
        const search = new URLSearchParams(
          getStartEndParamsByScopeType(type, selectedTimeframe, timezoneId)
        ).toString()
        // Navigate relative to the parent production-planning route so the feature works
        // regardless of where it is mounted (legacy hcem or Suite).
        void navigate({
          pathname: `../${getRouteSegmentForScope(type)}`,
          search
        })
      }}
    />
  )
}
